<template>
  <div class="flex flex-col gap-5">
    <CustomerForm
      action="edit"
      :customer_prop="customer"
      :disabled="!is_editable"
      @setCustomer="updateCustomer"
    >
      <template #general-actions>
        <ButtonSettings
          data-test="customer-dropdown"
          position="is-bottom-left"
          ellipsis_vertical
        >
          <div class="flex max-w-100 flex-col">
            <DropdownOption
              v-if="ProfileRecord.STATUS_ADDED === customer.attributes.status"
              custom
            >
              <div class="notification">
                {{ $t('contact.labels.inviteProfileFirstCustomer') }}
              </div>
            </DropdownOption>
            <DropdownOption
              data-test="customer-impersonate"
              @click="is_customer_impersonation_modal = true"
            >
              {{ $t('contact.actions.impersonate') }}
            </DropdownOption>
            <DropdownOption
              data-test="customer-resend-invitation-email"
              :disabled="ProfileRecord.STATUS_INVITED !== customer.attributes.status"
              @click="is_resend_invitation_modal = true"
            >
              {{ $t('contact.actions.resendInvitationEmail') }}
            </DropdownOption>
            <DropdownOption
              data-test="customer-delete"
              :disabled="current_profile.attributes.role === Employee.ROLE_MAINTAINER"
              @click="deleteCustomer"
            >
              {{ $t('contact.actions.deleteCustomer') }}
            </DropdownOption>
          </div>
        </ButtonSettings>
      </template>
      <template #customer-form-general-tags>
        <ContactStatus
          v-if="customer.id"
          :contact="customer"
          :is_show_timestamp="true"
          size="small"
        />
        <div
          v-for="unit in units_customer"
          :key="unit.id"
        >
          <AppLink
            class="tag rounded-full font-medium"
            v-tooltip.bottom="getUnitTooltip(unit)"
            :to="{
              name: ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS_INFORMATION,
              params: { project_id: unit.attributes.project_id, level_type: unit.type, level_id: unit.id },
            }"
          >
            {{ unit.attributes.name }}
          </AppLink>
        </div>
        <IntegrationIndicator :record="customer" />
      </template>
      <template #customer-form-header="{ submit, reset, isVisible }">
        <div
          v-if="isVisible"
          class="flex gap-2"
        >
          <ButtonPrimary
            action="save"
            data-test="customer-form-save-edit"
            @onClick="submit"
          >
            {{ $t('button.actions.save_changes') }}
          </ButtonPrimary>
          <ButtonTertiary @onClick="reset" />
        </div>
      </template>
      <template #customer-form-footer="{ submit, reset, isVisible }">
        <div
          v-if="isVisible"
          class="flex gap-2"
        >
          <ButtonPrimary
            action="save"
            @onClick="submit"
          >
            {{ $t('button.actions.save_changes') }}
          </ButtonPrimary>
          <ButtonTertiary @onClick="reset" />
        </div>
      </template>
    </CustomerForm>
    <UnitList
      :customer="customer"
      :group_customers="group_customers_customer"
      :projects="projects"
      :units="units_customer"
    />
    <CustomerInspirations :customer="customer" />

    <PrimeDialog
      v-model:visible="is_resend_invitation_modal"
      modal
      :header="$t('profile.labels.explainerSendEmail', { name: customer.attributes.name })"
      class="w-[30rem]"
    >
      <template #footer>
        <ButtonSecondary @click="is_resend_invitation_modal = false">
          {{ $t('button.actions.cancel') }}
        </ButtonSecondary>
        <ButtonPrimary @click="resendInvitationEmail">
          {{ $t('contact.actions.resendInvitationEmail') }}
        </ButtonPrimary>
      </template>
    </PrimeDialog>

    <CustomerImpersonationModal
      v-if="is_customer_impersonation_modal"
      :customer="customer"
      @close="is_customer_impersonation_modal = false"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import ContactStatus from '@/app/_employees/components/contacts/ContactStatus.vue';
import CustomerForm from '@/app/_employees/components/contacts/CustomerForm.vue';
import CustomerInspirations from '@/app/_employees/components/inspirations/TheCustomerInspirations.vue';
import IntegrationIndicator from '@/app/_employees/components/integrations/IntegrationIndicator.vue';
import UnitList from '@/app/_employees/components/contacts/TheUnitList.vue';
import useGlobalConfirm from '@/composables/useGlobalConfirm';
import CustomerImpersonationModal from '@/app/_employees/components/modals/TheCustomerImpersonationModal.vue';
import AppLink from '@/app/shared_components/AppLink.vue';
import { Employee, GroupCustomer, ProfileRecord } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { useToasts } from '@/app/util/toasts';
import DropdownOption from '@/app/shared_components/buttons/DropdownOption.vue';

const props = defineProps({
  customer: { type: Object, default: () => ({}) },
  projects: { type: Object, default: null },
});

const emit = defineEmits(['deleted', 'fetchCustomer']);

// Composables
const { t } = useI18n();
const { toast_action_succeeded, toast_action_failed } = useToasts();
const { confirm } = useGlobalConfirm();

// Reactive data
const is_resend_invitation_modal = ref(false);
const is_customer_impersonation_modal = ref(false);

// Store mappings
const { current_profile, current_client, customer_groups, group_customers, units } = mapGetters([
  'current_profile',
  'current_client',
  'customer_groups',
  'group_customers',
  'units',
]);

const { UPDATE_CUSTOMER, DELETE_CUSTOMER, SEND_EMAIL_INVITATION } = mapActions([
  'UPDATE_CUSTOMER',
  'DELETE_CUSTOMER',
  'SEND_EMAIL_INVITATION',
]);

// Computed properties
const is_editable = computed(() => current_profile.value.attributes.role === Employee.ROLE_OWNER);

const group_customers_customer = computed(() =>
  _.filter(group_customers.value, { attributes: { customer_id: props.customer.id } }),
);

const units_customer = computed(() => _.pick(units.value, _.map(group_customers_customer.value, 'attributes.unit_id')));

function getUnitTooltip(unit) {
  const project = props.projects[unit.attributes.project_id];
  const project_name = _.isNil(project) ? t('project.labels.noAccess') : project.attributes.name;
  const group_customer = _.find(group_customers_customer.value, { attributes: { unit_id: unit.id } });
  const scope_key = _.get(group_customer, 'attributes.scope') === GroupCustomer.SCOPE_VISIBLE ? 'visible' : 'invisible';
  return `${project_name} (${t(`unit.labels.scopes.${scope_key}`)})`;
}

// Watchers
watch(
  () => props.customer,
  (new_customer) => {
    if (new_customer) {
      is_customer_impersonation_modal.value = false;
    }
  },
);

// Methods

const updateCustomer = async (customer) => {
  try {
    const c = await UPDATE_CUSTOMER({ record: customer });
    toast_action_succeeded(t('toast.update.succeeded', { itemName: c.attributes.name }));
  } catch (error) {
    toast_action_failed({ error });
  }
};

const resendInvitationEmail = async () => {
  try {
    await SEND_EMAIL_INVITATION({ profile: props.customer });
    toast_action_succeeded(t('profile.toasts.emailResendSuccessfully'));
    emit('fetchCustomer', props.customer);
    is_resend_invitation_modal.value = false;
  } catch (error) {
    toast_action_failed({ error });
  }
};

const deleteCustomer = async () => {
  const has_units = group_customers_customer.value && group_customers_customer.value.length > 0;
  if (has_units) {
    await confirm({
      header: t('modal.labels.deleteDeniedTitle', { customer: props.customer.attributes.name }),
      message: t('modal.labels.deleteDeniedMessage'),
      accept_label: t('modal.actions.close'),
    });
    return;
  }

  const confirmed = await confirm({
    destructive: true,
    object_name: props.customer.attributes.name,
    header: t('modal.labels.deleteCustomerTitle', { customer: props.customer.attributes.name }),
    message: t('modal.labels.deleteCustomerMessage'),
  });
  if (!confirmed) return;

  try {
    await DELETE_CUSTOMER({ record: props.customer });
    emit('deleted', props.customer);
  } catch (error) {
    toast_action_failed({ error });
  }
};
</script>
